import { ReactNode } from 'react';

import {
  PageSectionFieldsFragment,
  BannerFieldsFragment,
  ShortcutLinksFieldsFragment,
  NewsSectionFieldsFragment,
  PhotoAndTextCardFieldsFragment,
  HeroBannerCarouselFieldsFragment,
  ExtraAddOnsFieldsFragment,
  SinglePackageHeaderFieldsFragment,
  TabGroupFieldsFragment,
  TraitsListFieldsFragment,
  PackageSuggestionFormFieldFragment,
  OrderPackageFormFieldsFragment,
  PageSectionsBySystemIdQuery,
  RichTextSectionFieldsFragment,
  MediaContainerFieldsFragment,
  StaggeredListFieldsFragment,
  QuotesCollectionFieldsFragment,
  SynCarouselFieldsFragment,
} from 'types/generated';
import Section from '@/components/Section';
import FaqSection from '@/components/FAQ/FaqSection';
import ShortcutLinks from '@/components/ShortcutLinks';
import CardCollection from '@/components/CardCollection';
import { getOr } from '@/utils/options';
import News from '@/components/News/News';
import PhotoAndTextCard from '@/components/PhotoAndTextCard';
import Banner from '@/components/Banner/index';
import HeroBannerCarousel from '@/components/Banner/HeroBannerCarousel';
import ExtraAddOns from '@/components/ExtraAddOns';
import SinglePackageHeader from '@/components/SinglePackageHeader';
import TabGroup from '@/components/Tabs/TabGroup';
import TraitsList from '@/components/TraitsList';
import PackageSuggestion from '@/components/PackageSuggestion';
import OrderPackageForm from '@/components/PackageForm';
import InfoSetSection from '@/components/InfoSetSection';
import RichTextSection from '@/components/RichTextSection/RichTextSection';
import MediaContainer from '@/components/MediaContainer/MediaContainer';
import StaggeredList from '@/components/StaggeredList/StaggeredList';
import SynCarousel from '@/components/SynCarousel';
import QuotesCollection from '@/components/QuotesCollection/QuotesCollection';

export function renderBanner(bannerFragment: BannerFieldsFragment): ReactNode {
  return <Banner data={bannerFragment} />;
}

function renderShortcut(shortcutFragment: ShortcutLinksFieldsFragment): ReactNode {
  return <ShortcutLinks data={shortcutFragment} />;
}

function renderCardCollection(cardCollectionId: string): ReactNode {
  return <CardCollection cardCollectionId={cardCollectionId} />;
}

function renderFaqSection(faqFragmentId: string): ReactNode {
  return <FaqSection id={faqFragmentId} />;
}

function renderNewsSection(newsFragment: NewsSectionFieldsFragment): ReactNode {
  return <News data={newsFragment} />;
}

export function renderPhotoAndTextCard(photoAndTextCardFragment: PhotoAndTextCardFieldsFragment): ReactNode {
  return <PhotoAndTextCard data={photoAndTextCardFragment} />;
}

function renderHeroBannerCarousel(heroBannerCarouselFragment: HeroBannerCarouselFieldsFragment): ReactNode {
  return <HeroBannerCarousel data={heroBannerCarouselFragment} />;
}

function renderExtraAddOns(extraAddOnsFragment: ExtraAddOnsFieldsFragment): ReactNode {
  return <ExtraAddOns data={extraAddOnsFragment} />;
}

function renderSinglePackageHeader(singlePackageHeaderFragment: SinglePackageHeaderFieldsFragment): ReactNode {
  return <SinglePackageHeader data={singlePackageHeaderFragment} />;
}

function renderTabGroup(tabGroupFragment: TabGroupFieldsFragment): ReactNode {
  return <TabGroup data={tabGroupFragment} />;
}

function renderTraitsList(traitsListFieldsFragment: TraitsListFieldsFragment): ReactNode {
  return <TraitsList data={traitsListFieldsFragment} />;
}

function renderPackageSuggestionForm(
  packageSuggestionFormFieldFragment: PackageSuggestionFormFieldFragment
): ReactNode {
  return <PackageSuggestion data={packageSuggestionFormFieldFragment} />;
}

function renderOrderPackageForm(orderPackageFormFieldsFragment: OrderPackageFormFieldsFragment): ReactNode {
  return <OrderPackageForm data={orderPackageFormFieldsFragment} />;
}

function renderInfoSet(infoSetId: string): ReactNode {
  return <InfoSetSection infoSetId={infoSetId} />;
}

function renderRichTextSection(richTextSectionFieldsFragment: RichTextSectionFieldsFragment): ReactNode {
  return <RichTextSection data={richTextSectionFieldsFragment} />;
}

function renderMediaContainer(mediaContainerFieldsFragment: MediaContainerFieldsFragment): ReactNode {
  return <MediaContainer data={mediaContainerFieldsFragment} />;
}

function renderStaggeredList(staggeredListFieldsFragment: StaggeredListFieldsFragment): ReactNode {
  return <StaggeredList data={staggeredListFieldsFragment} />;
}

function renderQuoteContainer(quotesCollectionFieldsFragments: QuotesCollectionFieldsFragment): ReactNode {
  return <QuotesCollection data={quotesCollectionFieldsFragments} />;
}

function renderSynCarousel(synCarouselFieldsFragment: SynCarouselFieldsFragment): ReactNode {
  return <SynCarousel data={synCarouselFieldsFragment} />;
}

export function renderSection(pageSectionFragment: PageSectionFieldsFragment, isFirst: boolean) {
  const childType: string = pageSectionFragment.children?.__typename || '';
  let child: ReactNode | null = null;

  switch (childType) {
    case 'SynCarousel':
      child = renderSynCarousel(pageSectionFragment.children as SynCarouselFieldsFragment);
      break;
    case 'Banner':
      child = renderBanner(pageSectionFragment.children as BannerFieldsFragment);
      break;
    case 'ShortcutLinks':
      child = renderShortcut(pageSectionFragment.children as ShortcutLinksFieldsFragment);
      break;
    case 'CardCollection':
      child = renderCardCollection((pageSectionFragment.children as { sys: { id: string } }).sys.id as string);
      break;
    case 'FaqSection':
      child = renderFaqSection((pageSectionFragment.children as { sys: { id: string } }).sys.id as string);
      break;
    case 'NewsSection':
      child = renderNewsSection(pageSectionFragment.children as NewsSectionFieldsFragment);
      break;
    case 'PhotoAndTextCard':
      child = renderPhotoAndTextCard(pageSectionFragment.children as PhotoAndTextCardFieldsFragment);
      break;
    case 'HeroBannerCarousel':
      child = renderHeroBannerCarousel(pageSectionFragment.children as HeroBannerCarouselFieldsFragment);
      break;
    case 'ExtraAddOns':
      child = renderExtraAddOns(pageSectionFragment.children as ExtraAddOnsFieldsFragment);
      break;
    case 'SinglePackageHeader':
      child = renderSinglePackageHeader(pageSectionFragment.children as SinglePackageHeaderFieldsFragment);
      break;
    case 'TabGroup':
      child = renderTabGroup(pageSectionFragment.children as TabGroupFieldsFragment);
      break;
    case 'TraitsList':
      child = renderTraitsList(pageSectionFragment.children as TraitsListFieldsFragment);
      break;
    case 'PackageSuggestionForm':
      child = renderPackageSuggestionForm(pageSectionFragment.children as PackageSuggestionFormFieldFragment);
      break;
    case 'OrderPackageForm':
      child = renderOrderPackageForm(pageSectionFragment.children as OrderPackageFormFieldsFragment);
      break;
    case 'InfoSet':
      child = renderInfoSet((pageSectionFragment.children as { sys: { id: string } }).sys.id as string);
      break;
    case 'RichTextSection':
      child = renderRichTextSection(pageSectionFragment.children as RichTextSectionFieldsFragment);
      break;
    case 'MediaContainer':
      child = renderMediaContainer(pageSectionFragment.children as MediaContainerFieldsFragment);
      break;
    case 'StaggeredList':
      child = renderStaggeredList(pageSectionFragment.children as StaggeredListFieldsFragment);
      break;
    case 'QuoteContainer':
      child = renderQuoteContainer(pageSectionFragment.children as QuotesCollectionFieldsFragment);
      break;
    default:
      console.log(`no rendering found for contentful type ${childType}`);
      throw new Error(`no rendering found for contentful type ${childType}`);
      break;
  }

  return (
    <Section
      childrenTypeName={pageSectionFragment.children?.__typename}
      sectionId={getOr(pageSectionFragment.id, undefined)}
      theme={getOr(pageSectionFragment.background, '')}
      marginTop={
        pageSectionFragment.marginTop === 0
          ? pageSectionFragment.marginTop
          : pageSectionFragment?.marginTop || (isFirst ? 128 : 32)
      }
      marginBottom={
        pageSectionFragment.marginBottom === 0
          ? pageSectionFragment.marginBottom
          : pageSectionFragment?.marginBottom || 32
      }
      gridType={getOr(pageSectionFragment.gridType as 'centered' | 'full', 'full')}
    >
      {child}
    </Section>
  );
}

export function render(q: PageSectionsBySystemIdQuery[], orderedIds: string[]) {
  // Collect all page sections into a single array
  const pageSections = q.flatMap((x) => x.pageSectionCollection?.items as PageSectionFieldsFragment[]);

  // Sort sections inline based on the order passed in by orderedIds
  pageSections.sort((a, b) => orderedIds.indexOf(a.sys.id) - orderedIds.indexOf(b.sys.id));

  return pageSections.map((s, index) => renderSection(s, index === 0));
}
